var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { Role } from "engage-common/types";
import { EngageAppMessage, EngageMessageType, } from "engage-common/messages";
import { useImmer } from "use-immer";
import { applyPatches, enablePatches } from "immer";
// @ts-ignore - no types for this library
import * as TimeSync from "timesync";
import ReactGA from "react-ga4";
export var ErrorType;
(function (ErrorType) {
    ErrorType[ErrorType["NoLobby"] = 0] = "NoLobby";
    ErrorType[ErrorType["FailCreate"] = 1] = "FailCreate";
    ErrorType[ErrorType["BadPassword"] = 2] = "BadPassword";
    ErrorType[ErrorType["Rejected"] = 3] = "Rejected";
})(ErrorType || (ErrorType = {}));
var UUIDKey = "Engage-UUID";
export var EngageContext;
var isDev = process.env.NODE_ENV !== "production";
// @ts-ignore
var socket = typeof window === "undefined" || window.STORYBOOK_ENV ? {} :
    isDev ? io("".concat(window.location.protocol, "//").concat(window.location.hostname, ":8080")) : io();
export function createEngageContext(initialAppState, initialUserState) {
    //Enable immer patching
    enablePatches();
    var preciseContext = createContext({
        appState: initialAppState,
        userState: initialUserState,
        sendMessage: function (message, ack) {
        },
        clearError: function () {
        },
        syncedTime: function () { return Date.now(); }
    });
    // @ts-ignore
    EngageContext = preciseContext;
    var EngageContextProvider = function (_a) {
        var _b;
        var children = _a.children;
        var _c = useState(), lobby = _c[0], setLobby = _c[1];
        var _d = useState(false), socketReady = _d[0], setSocketReady = _d[1];
        var _e = useState([]), messageQueue = _e[0], setMessageQueue = _e[1];
        var _f = useState(initialAppState), appState = _f[0], setAppState = _f[1];
        var _g = useState(initialUserState), userState = _g[0], setUserState = _g[1];
        var _h = useState({ nickname: "", id: "", role: Role.user }), user = _h[0], setUser = _h[1];
        var _j = useImmer({}), errorMap = _j[0], updateErrorMap = _j[1];
        var _k = useState(), lobbyValidation = _k[0], setLobbyValidation = _k[1];
        var timesync = useRef();
        var lobbyValidated = function (message) {
            setLobbyValidation(message);
        };
        var ackJoin = function (message) {
            localStorage.setItem(UUIDKey, message.user.id);
            setUser(message.user);
            setLobby(message.lobby);
            if (message.states) {
                setAppState(message.states);
            }
            if (message.user.role === Role.host) {
                ReactGA.event("Created-Lobby", { lobby_id: message.lobby.id, lobby_title: message.lobby.title });
            }
            else {
                ReactGA.event("Joined-Lobby", { lobby_id: message.lobby.id, lobby_title: message.lobby.title });
            }
        };
        var lobbyError = useCallback(function (message) {
            updateErrorMap(function (draft) {
                switch (message.subType) {
                    case EngageMessageType.CreateLobby:
                        draft[ErrorType.FailCreate] = "Lobby code rejected (duplicate or bad word): " + message.lobbyID;
                        break;
                    case EngageMessageType.LobbyJoined:
                        draft[ErrorType.NoLobby] = "No lobby found with code: " + message.lobbyID;
                        break;
                    case EngageMessageType.GetLobbyInfo:
                        draft[ErrorType.BadPassword] = "The password entered does not match.";
                        break;
                }
            });
        }, [updateErrorMap]);
        var receiveAppStateUpdate = useCallback(function (message) {
            setAppState(function (oldAppState) {
                var shallowCopy = __assign({}, oldAppState);
                for (var _i = 0, _a = Object.entries(message.patches); _i < _a.length; _i++) {
                    var _b = _a[_i], stateKey = _b[0], patches = _b[1];
                    if (shallowCopy[stateKey]) {
                        //@ts-ignore
                        shallowCopy[stateKey] = applyPatches(shallowCopy[stateKey], patches);
                    }
                }
                return shallowCopy;
            });
        }, []);
        function receiveHostLobbyUpdate(message) {
            setLobby(function (lobby) { return lobby ? applyPatches(lobby, message.hostLobbyPatches) : lobby; });
        }
        function receiveUserStateUpdate(message) {
            setUserState(function (userState) { return userState ? applyPatches(userState, message.userPatches) : userState; });
        }
        var lobbyModified = function (message) {
            setLobby(message.lobby);
        };
        var userModified = function (message) {
            setUser(message.user);
        };
        var lobbyKilled = function () {
            //Right now, just remove the UUID and refresh the page.
            localStorage.removeItem(UUIDKey);
            window.location.reload();
        };
        var clearError = useCallback(function (type) {
            updateErrorMap(function (draft) {
                delete draft[type];
            });
        }, [updateErrorMap]);
        var sendMessage = useCallback(function (message, callback, overrideReady) {
            if (overrideReady === void 0) { overrideReady = false; }
            if (socket && (socketReady || overrideReady)) {
                if (Object.values(EngageMessageType).includes(message.type)) {
                    socket.emit(message.type, message, callback);
                }
                else if (lobby) {
                    socket.emit(EngageAppMessage, Object.assign({}, message, { lobbyID: lobby.id }), callback);
                }
            }
            else {
                console.log("Queueing message: ", message.type);
                setMessageQueue(function (v) { return __spreadArray(__spreadArray([], v, true), [{ message: message, callback: callback }], false); });
            }
        }, [lobby, socketReady]);
        useEffect(function () {
            console.log("Setting up socket");
            setSocketReady(false);
            socket.removeAllListeners();
            socket.on(EngageMessageType.AppStateUpdated, receiveAppStateUpdate);
            socket.on(EngageMessageType.UserStateUpdated, receiveUserStateUpdate);
            socket.on(EngageMessageType.HostLobbyUpdated, receiveHostLobbyUpdate);
            socket.on(EngageMessageType.LobbyUpdated, lobbyModified);
            socket.on(EngageMessageType.LobbyValid, lobbyValidated);
            socket.on(EngageMessageType.UserUpdated, userModified);
            socket.on(EngageMessageType.LobbyJoined, ackJoin);
            socket.on(EngageMessageType.LobbyError, lobbyError);
            socket.on(EngageMessageType.LobbyKilled, lobbyKilled);
            //TimeSync setup
            var ts = TimeSync.create({ server: socket, interval: 300000 });
            ts.send = function (socket, data, timeout) {
                return new Promise(function (resolve, reject) {
                    var timeoutFn = setTimeout(reject, timeout);
                    socket === null || socket === void 0 ? void 0 : socket.emit("timesync", data, function () {
                        clearTimeout(timeoutFn);
                        resolve();
                    });
                });
            };
            timesync.current = ts;
            socket.on("timesync", function (data) {
                ts.receive(null, data);
            });
            //End TimeSync setup
            var localUUID = localStorage.getItem(UUIDKey);
            sendMessage({ type: EngageMessageType.Initiate, userID: localUUID, isRobot: false, autoJoin: false }, function (user, lobby, serverState, userState, rejected) {
                if (rejected === void 0) { rejected = false; }
                console.log("initiate received", user, lobby);
                if (rejected) {
                    updateErrorMap(function (draft) {
                        draft[ErrorType.Rejected] = "The server has reached its user limit.";
                    });
                }
                if (user) {
                    localStorage.setItem(UUIDKey, user.id);
                    setUser(user);
                    if (user.role === Role.host) {
                        ReactGA.event("Created-Lobby", { lobby_id: lobby.id, lobby_title: lobby.title });
                    }
                    else {
                        ReactGA.event("Joined-Lobby", { lobby_id: lobby.id, lobby_title: lobby.title });
                    }
                }
                if (lobby) {
                    setLobby(lobby);
                }
                if (userState) {
                    setUserState(userState);
                }
                if (serverState) {
                    setAppState(serverState);
                }
            }, true);
            for (var _i = 0, messageQueue_1 = messageQueue; _i < messageQueue_1.length; _i++) {
                var message = messageQueue_1[_i];
                sendMessage(message.message, message.callback, true);
            }
            setMessageQueue([]);
            setSocketReady(true);
        }, []);
        return React.createElement(EngageContext.Provider, { value: {
                lobby: lobby,
                userState: userState,
                user: user,
                // @ts-ignore
                sendMessage: sendMessage,
                errorMap: errorMap,
                clearError: clearError,
                lobbyValidation: lobbyValidation,
                syncedTime: (_b = timesync.current) === null || _b === void 0 ? void 0 : _b.now, appState: appState
            } }, children);
    };
    return {
        context: preciseContext,
        Provider: EngageContextProvider
    };
}
//# sourceMappingURL=context.js.map